import styled, { css, keyframes } from 'styled-components';

import { device } from '@/styles/helpers';

interface TournamentsProps {
  isMobile: boolean;
}

const changeSlide = keyframes`
  0%, 31% {
    top: 0px;
  }
  34%, 64% {
    top: -40px;
  }
  67%, 100% {
    top: -80px;
  }
`;

export const ScTournamentsWidget = styled.div<TournamentsProps>`
  width: 100%;
  overflow: hidden;

  ${({ isMobile }) =>
    isMobile &&
    css`
      position: absolute;
      top: 0;
      z-index: 114;
    `}

  .collapsed-banner__content {
    background-color: ${({ theme }) => theme.grayLightColor};
    padding: 0px 16px;
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    height: 24px;

    & > p {
      width: 100%;
      font-size: 12px;
      font-weight: 700;
      line-height: 18px;
    }

    & > i {
      transform: rotate(90deg);
      color: ${({ theme }) => theme.blackLightColor};
    }
  }

  .swiper-slide-active .slider .info-slide {
    animation: ${changeSlide} 16s infinite linear;
  }
`;

export interface ScTournamentsProps {
  selected?: boolean;
}

export const ScTournamentInfo = styled.div<TournamentsProps>`
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  background-color: ${({ theme }) => theme.grayLightColor};
  height: 40px;
  border-radius: 28px;
  padding: 0 0 0 16px;
  cursor: pointer;
  overflow: hidden;

  & > img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    flex-shrink: 0;
    margin-right: 8px;
  }

  .slider {
    width: 100%;
    height: 40px;

    .info-slide {
      position: relative;
      top: 0;
      width: 100%;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;

      & > p {
        width: 100%;
        text-align: center;
        display: flex;
        flex-direction: column;
        font-size: 16px;
        font-weight: 700;
        line-height: 100%;
        letter-spacing: 0.48px;
        text-transform: uppercase;

        span {
          font-size: 12px;
          font-weight: 400;
          line-height: 12px;
          text-transform: lowercase;
        }
      }
    }
  }

  & > .arrow {
    padding: 0 16px;
    transition: transform 0.3s;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    flex-shrink: 0;

    .arrow-icon {
      font-size: 2px;
      transform: rotate(-90deg);
      color: ${({ theme }) => theme.blackLightColor};
    }
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      border-radius: 0 0 16px 16px;
      height: 48px;

      & > img {
        width: 40px;
        height: 40px;
      }

      & > .arrow .arrow-icon {
        transform: rotate(90deg);
        font-size: 20px;
        color: ${({ theme }) => theme.whiteColor};
      }
    `}

  @media (hover: hover) {
    &:hover > .arrow .arrow-icon {
      color: ${({ theme }) => theme.whiteColor};
    }
  }
`;

export const ScTournamentDetailsModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;

  & > header {
    display: flex;
    gap: 8px;
    margin-top: 16px;

    .tournament-icon {
      width: 76px;
      height: 76px;
      object-fit: contain;
      flex-shrink: 0;
    }

    & > div {
      display: flex;
      flex-direction: column;
      gap: 16px;
      width: 100%;

      h2 {
        font-size: 32px;
        font-weight: 700;
        line-height: 40px;
        letter-spacing: 0.96px;
        text-transform: uppercase;
        margin-bottom: 0;
      }

      .details {
        display: flex;
        gap: 32px;
        align-items: center;

        & > div {
          display: flex;
          gap: 8px;
          align-items: center;
          color: ${({ theme }) => theme.blackColor};
          font-size: 12px;
          line-height: 18px;

          .icon {
            font-size: 20px;
            display: flex;
            align-items: center;
            justify-content: center;

            i {
              color: ${({ theme }) => theme.blackColor};
            }
          }
        }
      }
    }
  }

  .modal-section > h2 {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: 0.44px;
    text-transform: uppercase;
  }

  @media (${device.mobileS}) {
    & > header {
      flex-direction: column;
      gap: 16px;
      margin-top: 24px;

      h2 {
        font-size: 28px;
        line-height: 36px;
        letter-spacing: 0.84px;
        margin-bottom: 0;
      }

      & > div {
        flex-direction: row;
        align-items: flex-end;
        gap: 8px;

        .details {
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          width: 100%;

          & > div {
            color: ${({ theme }) => theme.whiteColor};

            .icon {
                i {
                  color: ${({ theme }) => theme.whiteColor};
                }
            }
          }
        }
      }
    }

    .modal-section > h2 {
      font-size: 20px;
      line-height: 26px;
      letter-spacing: 0.4px;
    }
  }
`;

interface ScTermsAndConditionsProps {
  isOpen: boolean;
}

export const ScTermsAndConditions = styled.div<ScTermsAndConditionsProps>`
  width: 100%;
  padding: 0 16px;

  .title {
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
    cursor: pointer;
    overflow: hidden;
    color: ${({ theme }) => theme.blackColor};

    .arrow {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      margin-left: 8px;


      & > i {
        font-size: 16px;
        transition: transform 0.25s ease-in-out;
        transform: ${({ isOpen }) =>
          isOpen
            ? 'rotate(-90deg) translateY(0) scale(1)'
            : 'rotate(90deg) translateY(0)'};

        color: ${({ theme }) => theme.blackColor};
      }
    }

    @media (hover: hover) {
      &:hover .arrow > i {
        transform: ${({ isOpen }) =>
          `rotate(${isOpen ? '-90deg' : '90deg'}) translateY(2px)`};
      }
    }

    @media (${device.mobileS}) {
      color: ${({ theme }) => theme.whiteColor};

      .arrow > i {
        color: ${({ theme }) => theme.whiteColor};
      }
    }
  }
`;
